// ---------------------------------------------------------------------------
// A page's state without functional-lit (R16b)
//
// The old pages are @adornis/functional-lit components: hooks keyed by the
// order of their calls, an effect run on a timer after each render, and one
// bug that matters (found in R17a): a setState that reaches a component after
// it was unmounted brings it back to life. stateful.js deletes the hooks on
// disconnect, but the setState still holds the component and calls
// rerender(); the render finds no hooks, starts from the initial state and
// schedules every effect again, so a page nobody sees loads, polls and
// listens until the tab is closed.
//
// definePage() is the replacement, without Adornis:
//
//   - The page is a directive, as before: CampaignDetail({ campaignId })
//     renders into the outlet's part with no element of its own, so the
//     markup is exactly what the view writes.
//   - Its state lives in a PageHost, one per mounted page: page.state(key,
//     initial) returns the value and a setter, by name, not by call order. A
//     key declared twice in one render throws.
//   - A setter stores the value and renders the page again in a microtask, as
//     a LitElement does: several setters in a row render once. The outlet's
//     own render (a new route, new props) renders it at once.
//   - Beside the view run reactive controllers (Lit's ReactiveController:
//     hostConnected, hostUpdated, hostDisconnected), for what the page does on
//     its own: its loads when it opens, its polling.
//   - Leaving the page (the outlet's remount disconnects it) ends it for good:
//     every controller is told, and a setter called later stores nothing and
//     renders nothing. The page writes one console line the first time that
//     happens, under its own prefix, so the field shows late answers being
//     dropped. Put back into the page (Lit's reconnect), it starts as a new
//     page, as functional-lit's did.
//   - A view that sets its own state while it renders throws, as functional-lit
//     did (it would render forever otherwise).
//   - A view that throws is written to the console; on the outlet's render
//     its message stands in its place, on a setter's render the page keeps
//     what it showed (functional-lit did both the same way).
// ---------------------------------------------------------------------------

import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { AsyncDirective, type DirectiveResult, directive } from 'lit/async-directive.js';
import { type ChildPart, type PartInfo, PartType } from 'lit/directive.js';

/** How a page is defined: its console prefix, what makes new props a new page, its controllers. */
export interface PageSpec<P, C> {
  /** The prefix of the page's console lines, e.g. "[campaign-detail]". */
  name: string;
  /** Whether new props start a new page with a fresh state. Without it, props never do. */
  newPage?(before: P, after: P): boolean;
  /** Called once for each page that is mounted: what runs beside its view (its controllers). */
  create(host: PageHost): C;
}

export type PageView<P, C> = (page: PageHost, own: C, props: P) => unknown;

/** One mounted page: its state, its controllers, whether it was left. */
export class PageHost implements ReactiveControllerHost {
  readonly #values = new Map<string, unknown>();
  readonly #setters = new Map<string, (value: never) => void>();
  readonly #controllers = new Set<ReactiveController>();
  /** The keys declared in the render running now. */
  readonly #declared = new Set<string>();
  #pending = false;
  #rendering = false;
  #left = false;
  #warned = false;
  #done: Promise<boolean> = Promise.resolve(true);

  readonly #name: string;
  /** Renders the page again and commits it, outside the outlet's render. */
  readonly #commit: () => void;

  constructor(name: string, commit: () => void) {
    this.#name = name;
    this.#commit = commit;
  }

  /** The page's state named `key`: its value now, and a setter that renders the page again. */
  state<T>(key: string, initial: T): [T, (value: T) => void] {
    if (this.#declared.has(key)) throw new Error(`${this.#name} the state "${key}" is declared twice`);
    this.#declared.add(key);
    if (!this.#values.has(key)) this.#values.set(key, initial);
    let set = this.#setters.get(key) as ((value: T) => void) | undefined;
    if (!set) {
      set = (value: T) => {
        if (this.#left) {
          this.#late(key);
          return;
        }
        // As functional-lit refused it: a view that sets its own state renders forever.
        if (this.#rendering) throw new Error(`${this.#name} the state "${key}" was set while the page rendered`);
        this.#values.set(key, value);
        this.requestUpdate();
      };
      this.#setters.set(key, set as (value: never) => void);
    }
    return [this.#values.get(key) as T, set];
  }

  /** False once the page was left. */
  get mounted(): boolean {
    return !this.#left;
  }

  addController(controller: ReactiveController): void {
    this.#controllers.add(controller);
  }

  removeController(controller: ReactiveController): void {
    this.#controllers.delete(controller);
  }

  /** Renders the page again in a microtask, once for every change made before it. */
  requestUpdate(): void {
    if (this.#left || this.#pending) return;
    this.#pending = true;
    this.#done = new Promise<boolean>(resolve => {
      queueMicrotask(() => {
        this.#pending = false;
        if (!this.#left) this.#commit();
        resolve(!this.#left);
      });
    });
  }

  /** Resolves once the render asked for has run: true, or false when the page was left before it. */
  get updateComplete(): Promise<boolean> {
    return this.#done;
  }

  /** Runs one render of the view: the keys it declares are counted afresh. */
  render(view: () => unknown): unknown {
    this.#declared.clear();
    this.#rendering = true;
    try {
      return view();
    } finally {
      this.#rendering = false;
      this.#declared.clear();
    }
  }

  /** The page is in the document. */
  connect(): void {
    for (const c of [...this.#controllers]) c.hostConnected?.();
  }

  /** A render was committed. */
  updated(): void {
    if (this.#left) return;
    for (const c of [...this.#controllers]) c.hostUpdated?.();
  }

  /** The page was left: for good. */
  leave(): void {
    if (this.#left) return;
    this.#left = true;
    for (const c of [...this.#controllers]) c.hostDisconnected?.();
  }

  #late(key: string): void {
    if (this.#warned) return;
    this.#warned = true;
    console.warn(`${this.#name} a change after the page was left (${key}) was dropped; nothing loads again`);
  }
}

/**
 * A page as a directive: `definePage(spec, view)` returns the function the outlet calls with the page's
 * props, as functional-lit's `stateful` did.
 */
export function definePage<P, C>(spec: PageSpec<P, C>, view: PageView<P, C>): (props: P) => DirectiveResult {
  class Page extends AsyncDirective {
    #host: PageHost | undefined;
    #own: C | undefined;
    #props: P | undefined;

    constructor(partInfo: PartInfo) {
      super(partInfo);
      if (partInfo.type !== PartType.CHILD) throw new Error(`${spec.name} a page belongs in a child part`);
    }

    /** A new page: a fresh state, its controllers, connected. */
    #mount(props: P): void {
      this.#host?.leave();
      const host = new PageHost(spec.name, () => {
        if (this.#host !== host) return;
        const shown = this.#view(false);
        if (shown.ok) this.setValue(shown.value);
        this.#afterCommit(host);
      });
      this.#host = host;
      this.#props = props;
      this.#own = spec.create(host);
      host.connect();
    }

    #view(onOutlet: boolean): { ok: boolean; value: unknown } {
      const host = this.#host as PageHost;
      try {
        return { ok: true, value: host.render(() => view(host, this.#own as C, this.#props as P)) };
      } catch (error) {
        console.error(error);
        return { ok: onOutlet, value: error instanceof Error ? error.message : error };
      }
    }

    /** The controllers hear of a render once it is in the document: after the part has committed it. */
    #afterCommit(host: PageHost): void {
      queueMicrotask(() => {
        if (this.#host === host) host.updated();
      });
    }

    render(_props: P): unknown {
      // Lit calls render() only from the update() this class replaces.
      return this.#host ? this.#view(true).value : undefined;
    }

    override update(_part: ChildPart, [props]: [P]): unknown {
      const before = this.#props;
      if (!this.#host || (before !== undefined && spec.newPage?.(before, props))) this.#mount(props);
      else this.#props = props;
      const shown = this.#view(true);
      this.#afterCommit(this.#host as PageHost);
      return shown.value;
    }

    protected override disconnected(): void {
      this.#host?.leave();
    }

    protected override reconnected(): void {
      // Put back into the page: a new page, as functional-lit's (its hooks were gone).
      this.#mount(this.#props as P);
      const shown = this.#view(true);
      this.setValue(shown.value);
      this.#afterCommit(this.#host as PageHost);
    }
  }
  const page = directive(Page);
  return (props: P) => page(props);
}
